/**
 * An announcement (TMMPDM-0110; TMMAPI-0181 item 32a): `/tickets/announcements/new` and
 * `/tickets/announcements/:id`. A draft opens in the editor; a scheduled, published or expired one
 * opens read-only with its actions (Unpublish, Expire now), because only a draft is edited.
 *
 * The editor: title (≤ 120), body (≤ 8000, the small Markdown subset) with Edit / Preview, kind,
 * audience (all customers, or chosen tenants), when to publish (**Now**, the default, sends no date;
 * Schedule takes a future time, checked here first), an optional expiry, pinned, pop-up (with its
 * warning) and a related feature request. Save draft keeps it; Publish… / Schedule… confirms with
 * the audience and whether it pops up, saves, then publishes. The leak guard is the server's: 422
 * `ANNOUNCEMENT_TERM_REFUSED {term}` names the term on the body field, and the confirm stays open.
 *
 * TMMPDM-0111 (0181 U5 YAML): only a draft is changed, published or deleted; 409
 * `ANNOUNCEMENT_NOT_DRAFT` (it moved on meanwhile) and Expire's 409 `ANNOUNCEMENT_NOT_PUBLISHED
 * {state}` are worded inside their dialog, whose confirm is then disabled; closing it re-reads the
 * announcement. Save draft has no dialog: its refusal re-reads at once and is shown above the result.
 * A missing `authorName` reads "Staff", never an email.
 *
 * Until tmm-api deploys item 32a a bare 404 reads "Announcements aren't available yet."
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { adminApi } from '@/api/tmm/admin';
import { announcementNotPublishedState, announcementRefusedTerm, errorCopy } from '@/api/errors';
import {
  ANNOUNCEMENT_KINDS,
  type AnnouncementAudience,
  type AnnouncementKind,
  type AnnouncementState,
  type AnnouncementView,
  type AnnouncementWrite,
} from '@/api/tmm/types';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  FormPage,
  FormSection,
  PageHeader,
  SectionCard,
  useNotify,
  type ErrorSummaryItem,
} from '@/components/page';
import type { HeaderAction } from '@/components/page/headerChrome';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { formatInstant, formatNumber } from '@/format';
import { StatusChip } from '@/status';
import { MarkdownPreview } from './MarkdownPreview';
import { parseTicketNumber, ticketRef, type Translate } from './tickets';
import {
  ANNOUNCEMENTS_TAB,
  BODY_MAX,
  TITLE_MAX,
  announcementPath,
  audienceLabel,
  instantToLocalInput,
  isAnnouncementNotDraft,
  isAnnouncementNotFound,
  isAnnouncementsNotDeployed,
  kindLabel,
  localInputToInstant,
  parseAnnouncementId,
  stateLabel,
} from './announcements';

type Load =
  | { state: 'loading' }
  | { state: 'new' }
  | { state: 'loaded'; item: AnnouncementView }
  | { state: 'notFound' }
  | { state: 'notDeployed' }
  | { state: 'failed'; failure: unknown };

export function AnnouncementEditPage() {
  const { id: param = '' } = useParams();
  const parsed = parseAnnouncementId(param);
  const { t } = useCopy();
  const [reloadToken, setReloadToken] = useState(0);
  // The id this page created (a draft saved from "new"): the URL moves to it without a re-read.
  const [createdId, setCreatedId] = useState<number | null>(null);
  const [load, setLoad] = useState<Load>(
    parsed === null
      ? { state: 'notFound' }
      : parsed === 'new'
        ? { state: 'new' }
        : { state: 'loading' },
  );

  useEffect(() => {
    if (typeof parsed !== 'number' || parsed === createdId) return;
    let cancelled = false;
    adminApi
      .getAnnouncement(parsed)
      .then((item) => {
        if (!cancelled) setLoad({ state: 'loaded', item });
      })
      .catch((failure: unknown) => {
        if (cancelled) return;
        if (isAnnouncementNotFound(failure)) setLoad({ state: 'notFound' });
        else if (isAnnouncementsNotDeployed(failure)) setLoad({ state: 'notDeployed' });
        else setLoad({ state: 'failed', failure });
      });
    return () => {
      cancelled = true;
    };
  }, [parsed, createdId, reloadToken]);

  const crumbs = [
    { label: t('page.tickets.title', 'Support'), to: '/tickets' },
    { label: t('tickets.tab.announcements', 'Announcements'), to: ANNOUNCEMENTS_TAB },
  ];
  // Words to show above the re-read announcement, after a refusal that re-read it (TMMPDM-0111).
  const [notice, setNotice] = useState<string | null>(null);
  const reread = () => {
    setNotice(null);
    setReloadToken((n) => n + 1);
  };
  // The server refused because it moved on: read it again, even the draft this page created.
  const rereadAfterRefusal = (words: string | null) => {
    setNotice(words);
    setCreatedId(null);
    setReloadToken((n) => n + 1);
  };

  if (load.state === 'new' || (load.state === 'loaded' && load.item.state === 'DRAFT')) {
    // One editor per draft; the draft this editor created keeps it (and an open confirm) mounted.
    const editorKey =
      load.state === 'new' || load.item.id === createdId ? 'new' : String(load.item.id);
    return (
      <AnnouncementEditor
        key={editorKey}
        crumbs={crumbs}
        initial={load.state === 'loaded' ? load.item : null}
        onCreated={(item) => {
          setCreatedId(item.id);
          setLoad({ state: 'loaded', item });
        }}
        onChanged={(item) => setLoad({ state: 'loaded', item })}
        onStale={rereadAfterRefusal}
      />
    );
  }
  if (load.state === 'loaded') {
    return (
      <AnnouncementReadOnly crumbs={crumbs} item={load.item} notice={notice} onChanged={reread} />
    );
  }

  const title =
    load.state === 'notFound'
      ? t('announcements.notFound', "This announcement doesn't exist.")
      : t('tickets.tab.announcements', 'Announcements');
  return (
    <Box>
      <PageHeader breadcrumbs={[...crumbs, { label: param || title }]} title={title} />
      {load.state === 'loading' ? <LoadingSpinner /> : null}
      {load.state === 'notDeployed' ? (
        <EmptyState
          surface
          title={t('announcements.notAvailable', "Announcements aren't available yet.")}
        />
      ) : null}
      {load.state === 'failed' ? (
        <ErrorState
          error={load.failure}
          message={t('announcements.itemLoadError', 'Could not load this announcement.')}
          onRetry={reread}
        />
      ) : null}
    </Box>
  );
}

type Crumbs = Array<{ label: string; to?: string }>;

/** A feature request to relate: its number, and its title once the picker has read it. */
interface RelatedOption {
  number: number;
  title?: string;
}

interface Draft {
  title: string;
  body: string;
  kind: AnnouncementKind;
  audience: AnnouncementAudience;
  tenantIds: number[];
  when: 'now' | 'schedule';
  publishAt: string;
  expires: string;
  pinned: boolean;
  popup: boolean;
  related: RelatedOption | null;
}

function draftOf(item: AnnouncementView | null): Draft {
  return {
    title: item?.title ?? '',
    body: item?.body ?? '',
    kind: (ANNOUNCEMENT_KINDS as readonly string[]).includes(item?.kind ?? '')
      ? (item?.kind as AnnouncementKind)
      : 'NEWS',
    audience: item?.audience === 'TENANTS' ? 'TENANTS' : 'ALL',
    tenantIds: item?.tenantIds ?? [],
    // Now is the default: a draft carries no publish time.
    when: 'now',
    publishAt: '',
    expires: instantToLocalInput(item?.expiresAt),
    pinned: item?.pinned ?? false,
    popup: item?.popup ?? false,
    related: item?.relatedTicket === undefined ? null : { number: item.relatedTicket },
  };
}

/** The editable fields on the wire; optional ones only when set (never null). */
function toWrite(draft: Draft): AnnouncementWrite {
  const expiresAt = draft.expires === '' ? null : localInputToInstant(draft.expires);
  return {
    title: draft.title.trim(),
    body: draft.body,
    kind: draft.kind,
    audience: draft.audience,
    ...(draft.audience === 'TENANTS' ? { tenantIds: draft.tenantIds } : {}),
    ...(expiresAt !== null ? { expiresAt } : {}),
    pinned: draft.pinned,
    popup: draft.popup,
    ...(draft.related !== null ? { relatedTicket: draft.related.number } : {}),
  };
}

type FieldKey = 'title' | 'body' | 'tenantIds' | 'publishAt' | 'expires';
type FieldErrors = Partial<Record<FieldKey, string>>;

const FIELD_ID: Record<FieldKey, string> = {
  title: 'announcement-title',
  body: 'announcement-body',
  tenantIds: 'announcement-tenants',
  publishAt: 'announcement-publish-at',
  expires: 'announcement-expires',
};

/** The client's checks: a draft needs its title and body; publishing also needs a future time. */
function validate(draft: Draft, publishing: boolean, t: Translate, now = Date.now()): FieldErrors {
  const errors: FieldErrors = {};
  if (draft.title.trim() === '')
    errors.title = t('announcements.editor.titleRequired', 'Enter a title.');
  if (draft.body.trim() === '')
    errors.body = t('announcements.editor.bodyRequired', 'Write the announcement.');
  if (draft.audience === 'TENANTS' && draft.tenantIds.length === 0) {
    errors.tenantIds = t('announcements.editor.tenantsRequired', 'Choose at least one tenant.');
  }
  let publishAt: number | null = null;
  if (publishing && draft.when === 'schedule') {
    const instant = localInputToInstant(draft.publishAt);
    if (instant === null) {
      errors.publishAt = t('announcements.editor.publishAtRequired', 'Choose a date and time.');
    } else if (Date.parse(instant) <= now) {
      errors.publishAt = t(
        'announcements.editor.publishAtPast',
        'Choose a time in the future, or pick Now.',
      );
    } else {
      publishAt = Date.parse(instant);
    }
  }
  if (draft.expires !== '') {
    const instant = localInputToInstant(draft.expires);
    if (instant === null || Date.parse(instant) <= now) {
      errors.expires = t('announcements.editor.expiresPast', 'Choose a time in the future.');
    } else if (publishAt !== null && Date.parse(instant) <= publishAt) {
      errors.expires = t(
        'announcements.editor.expiresBeforePublish',
        'Choose a time after it publishes.',
      );
    }
  }
  return errors;
}

/** Feature requests matching the typed text; a typed number (FR-1002 or 1002) is read directly. */
function useFeatureRequestOptions(input: string): RelatedOption[] {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<RelatedOption[]>([]);
  useEffect(() => {
    const handle = window.setTimeout(() => setQuery(input.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [input]);
  useEffect(() => {
    let cancelled = false;
    const number = query === '' ? null : parseTicketNumber(query);
    Promise.all([
      adminApi
        .listTickets({
          type: 'FEATURE_REQUEST',
          ...(query !== '' && number === null ? { q: query } : {}),
          page: 0,
          size: 10,
        })
        .then((page) => page.tickets)
        .catch(() => []),
      number === null ? Promise.resolve(null) : adminApi.getTicket(number).catch(() => null),
    ]).then(([rows, direct]) => {
      if (cancelled) return;
      const found = direct !== null && direct.type === 'FEATURE_REQUEST' ? [direct, ...rows] : rows;
      const seen = new Set<number>();
      setOptions(
        found
          .filter((row) => {
            if (seen.has(row.number)) return false;
            seen.add(row.number);
            return true;
          })
          .map((row) => ({ number: row.number, title: row.title })),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [query]);
  return options;
}

interface EditorProps {
  crumbs: Crumbs;
  initial: AnnouncementView | null;
  onCreated: (item: AnnouncementView) => void;
  onChanged: (item: AnnouncementView) => void;
  /** It's no longer a draft (409 `ANNOUNCEMENT_NOT_DRAFT`): re-read it, with the words to show, if any. */
  onStale: (words: string | null) => void;
}

function AnnouncementEditor({ crumbs, initial, onCreated, onChanged, onStale }: EditorProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const notify = useNotify();
  const navigate = useNavigate();
  const [id, setId] = useState<number | null>(initial?.id ?? null);
  const [draft, setDraft] = useState<Draft>(() => draftOf(initial));
  const [saved, setSaved] = useState<string>(() => JSON.stringify(toWrite(draftOf(initial))));
  const [view, setView] = useState<'edit' | 'preview'>('edit');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // A dialog's action was refused because it's no longer a draft: its confirm is off; closing re-reads.
  const [stale, setStale] = useState(false);
  const [tenants, setTenants] = useState<Array<{ id: number; name: string }>>([]);
  const [tenantsFailed, setTenantsFailed] = useState(false);
  const [relatedInput, setRelatedInput] = useState('');
  const relatedOptions = useFeatureRequestOptions(relatedInput);
  // After a first save, the URL moves to the new draft once the page no longer counts it unsaved.
  const [moveTo, setMoveTo] = useState<number | null>(null);

  const snapshot = JSON.stringify(toWrite(draft));
  const dirty = snapshot !== saved;
  const name = draft.title.trim() || t('announcements.new', 'New announcement');
  const guard = useUnsavedChangesGuard(dirty, name);

  const moved = useRef<number | null>(null);
  useEffect(() => {
    if (moveTo === null || dirty || moved.current === moveTo) return;
    moved.current = moveTo;
    void navigate(announcementPath(moveTo), { replace: true });
  }, [moveTo, dirty, navigate]);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listTenants({ size: 200 })
      .then((list) => {
        if (!cancelled)
          setTenants(list.tenants.map((tenant) => ({ id: tenant.id, name: tenant.name })));
      })
      .catch(() => {
        if (!cancelled) setTenantsFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const tenantNames = useMemo(
    () => new Map(tenants.map((tenant) => [tenant.id, tenant.name])),
    [tenants],
  );
  const chosenTenants = draft.tenantIds.map((tenantId) => ({
    id: tenantId,
    name: tenantNames.get(tenantId) ?? String(tenantId),
  }));

  const change = <K extends keyof Draft>(key: K, value: Draft[K], clears?: FieldKey) => {
    setDraft((current) => ({ ...current, [key]: value }));
    if (clears !== undefined) setErrors(({ [clears]: _dropped, ...others }) => others);
  };

  const summaryItems: ErrorSummaryItem[] = (Object.keys(errors) as FieldKey[])
    .filter((key) => errors[key] !== undefined)
    .map((key) => ({
      id: FIELD_ID[key],
      label: fieldLabel(key, t),
      message: errors[key] ?? '',
    }));

  const check = (publishing: boolean): boolean => {
    const found = validate(draft, publishing, t);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setAttempt((n) => n + 1);
      return false;
    }
    return true;
  };

  /** Creates or updates the draft; returns it, or throws the server's refusal. */
  const persist = async (): Promise<AnnouncementView> => {
    const body = toWrite(draft);
    const item =
      id === null
        ? await adminApi.createAnnouncement(body)
        : await adminApi.updateAnnouncement(id, body);
    setSaved(JSON.stringify(body));
    if (id === null) {
      setId(item.id);
      onCreated(item);
    }
    return item;
  };

  /** A refused term marks the body (in Edit, so the field shows) and returns its words. */
  const refusalText = (failure: unknown, fallback: string): string => {
    const term = announcementRefusedTerm(failure);
    if (term === null) return errorCopy(failure, t, fallback);
    const words = t(
      'announcements.editor.termRefused',
      'This mentions "{{term}}", which is internal. Remove it to publish.',
      { term },
    );
    setErrors((current) => ({ ...current, body: words }));
    setView('edit');
    return words;
  };

  const saveDraft = async () => {
    if (!check(false)) return;
    setBusy(true);
    setFormError(null);
    try {
      const item = await persist();
      // A first save: the page moves to the new draft's address.
      if (id === null) setMoveTo(item.id);
      else onChanged(item);
      notify.success(t('announcements.editor.saved', 'Draft saved.'));
    } catch (failure: unknown) {
      if (isAnnouncementNotDraft(failure)) {
        onStale(
          errorCopy(failure, t, t('announcements.editor.saveError', "Couldn't save the draft.")),
        );
        return;
      }
      setFormError(
        refusalText(failure, t('announcements.editor.saveError', "Couldn't save the draft.")),
      );
    } finally {
      setBusy(false);
    }
  };

  const publishAtInstant = draft.when === 'schedule' ? localInputToInstant(draft.publishAt) : null;

  const publish = async () => {
    setBusy(true);
    setConfirmError(null);
    let created: number | null = null;
    try {
      const item = await persist();
      if (id === null) created = item.id;
      await adminApi.publishAnnouncement(
        item.id,
        publishAtInstant === null ? {} : { publishAt: publishAtInstant },
      );
      notify.success(
        publishAtInstant === null
          ? t('announcements.confirm.published', 'Announcement published.')
          : t('announcements.confirm.scheduled', 'Announcement scheduled.'),
      );
      guard.leave(ANNOUNCEMENTS_TAB, { replace: true });
    } catch (failure: unknown) {
      // The confirm stays open with the refusal; a refused term is marked on the body too. A draft
      // saved on the way stays, and the page moves to its address.
      if (created !== null) setMoveTo(created);
      if (isAnnouncementNotDraft(failure)) setStale(true);
      setConfirmError(
        refusalText(failure, t('announcements.confirm.error', "Couldn't publish it.")),
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (id === null) return;
    setBusy(true);
    setDeleteError(null);
    try {
      await adminApi.deleteAnnouncement(id);
      notify.success(t('announcements.delete.done', 'Draft deleted.'));
      guard.leave(ANNOUNCEMENTS_TAB, { replace: true });
    } catch (failure: unknown) {
      if (isAnnouncementNotDraft(failure)) setStale(true);
      setDeleteError(
        errorCopy(failure, t, t('announcements.delete.error', "Couldn't delete the draft.")),
      );
    } finally {
      setBusy(false);
    }
  };

  const scheduled = draft.when === 'schedule';
  const pageTitle = initial?.title ?? t('announcements.new', 'New announcement');
  const confirmTitle = scheduled
    ? t('announcements.confirm.scheduleTitle', 'Schedule "{{title}}"?', {
        title: draft.title.trim(),
      })
    : t('announcements.confirm.publishTitle', 'Publish "{{title}}"?', {
        title: draft.title.trim(),
      });

  return (
    <>
      <PageHeader breadcrumbs={[...crumbs, { label: pageTitle }]} title={pageTitle} />
      <FormPage
        headerless
        title={pageTitle}
        onSubmit={() => {
          if (check(true)) {
            setConfirmError(null);
            setConfirming(true);
          }
        }}
        busy={busy}
        submitLabel={
          scheduled
            ? t('announcements.editor.scheduleAction', 'Schedule…')
            : t('announcements.editor.publishAction', 'Publish…')
        }
        onCancel={() => void navigate(ANNOUNCEMENTS_TAB)}
        guard={guard}
        errorSummary={{ items: summaryItems, attempt }}
        {...(formError !== null ? { formError: <Alert severity="error">{formError}</Alert> } : {})}
        actions={
          <>
            {id !== null ? (
              <Button color="error" onClick={() => setDeleting(true)} disabled={busy}>
                {t('announcements.delete.action', 'Delete draft…')}
              </Button>
            ) : null}
            <Button variant="outlined" onClick={() => void saveDraft()} disabled={busy}>
              {t('announcements.editor.saveDraft', 'Save draft')}
            </Button>
          </>
        }
      >
        <FormSection title={t('announcements.editor.sectionContent', 'Content')}>
          <TextField
            id={FIELD_ID.title}
            label={t('announcements.editor.title', 'Title')}
            value={draft.title}
            onChange={(event) => change('title', event.target.value, 'title')}
            required
            fullWidth
            error={errors.title !== undefined}
            helperText={
              errors.title ??
              t('tickets.composer.counter', '{{used, number}} of {{max, number}} characters', {
                used: draft.title.length,
                max: TITLE_MAX,
              })
            }
            slotProps={{ htmlInput: { maxLength: TITLE_MAX } }}
          />
          <Box>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              onChange={(_event, next: 'edit' | 'preview' | null) => {
                if (next !== null) setView(next);
              }}
              aria-label={t('announcements.editor.bodyView', 'Body view')}
              sx={{ mb: 1.5 }}
            >
              <ToggleButton value="edit">{t('announcements.editor.edit', 'Edit')}</ToggleButton>
              <ToggleButton value="preview">
                {t('announcements.editor.preview', 'Preview')}
              </ToggleButton>
            </ToggleButtonGroup>
            {view === 'edit' ? (
              <TextField
                id={FIELD_ID.body}
                label={t('announcements.editor.body', 'Body')}
                value={draft.body}
                onChange={(event) => change('body', event.target.value, 'body')}
                required
                fullWidth
                multiline
                minRows={8}
                error={errors.body !== undefined}
                helperText={
                  <>
                    {errors.body ?? (
                      <>
                        {t(
                          'announcements.editor.bodyHint',
                          "Markdown: # headings, **bold**, *italic*, lists and [links](https://…). {productName} becomes each customer's product name.",
                        )}{' '}
                        {t(
                          'tickets.composer.counter',
                          '{{used, number}} of {{max, number}} characters',
                          {
                            used: draft.body.length,
                            max: BODY_MAX,
                          },
                        )}
                      </>
                    )}
                  </>
                }
                slotProps={{ htmlInput: { maxLength: BODY_MAX } }}
              />
            ) : (
              <Paper
                variant="outlined"
                role="region"
                aria-label={t('announcements.editor.preview', 'Preview')}
                sx={{ p: 2 }}
              >
                {draft.body.trim() === '' ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('announcements.editor.previewEmpty', 'Nothing to preview yet.')}
                  </Typography>
                ) : (
                  <MarkdownPreview source={draft.body} />
                )}
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 2 }}>
                  {t(
                    'announcements.editor.previewNote',
                    "{productName} shows as typed here; each customer sees their product's name.",
                  )}
                </Typography>
              </Paper>
            )}
          </Box>
          <TextField
            select
            label={t('announcements.editor.kind', 'Kind')}
            value={draft.kind}
            onChange={(event) => {
              const kind = ANNOUNCEMENT_KINDS.find((code) => code === event.target.value);
              if (kind) change('kind', kind);
            }}
            sx={{ maxWidth: 320 }}
          >
            {ANNOUNCEMENT_KINDS.map((code) => (
              <MenuItem key={code} value={code}>
                {kindLabel(code, t)}
              </MenuItem>
            ))}
          </TextField>
        </FormSection>

        <FormSection title={t('announcements.editor.sectionAudience', 'Audience and timing')}>
          <FormControl>
            <FormLabel id="announcement-audience-label">
              {t('announcements.editor.audience', 'Audience')}
            </FormLabel>
            <RadioGroup
              aria-labelledby="announcement-audience-label"
              value={draft.audience}
              onChange={(event) =>
                change(
                  'audience',
                  event.target.value === 'TENANTS' ? 'TENANTS' : 'ALL',
                  'tenantIds',
                )
              }
            >
              <FormControlLabel
                value="ALL"
                control={<Radio />}
                label={t('announcements.audienceAll', 'All customers')}
              />
              <FormControlLabel
                value="TENANTS"
                control={<Radio />}
                label={t('announcements.editor.audienceChosen', 'Chosen tenants')}
              />
            </RadioGroup>
          </FormControl>
          {draft.audience === 'TENANTS' ? (
            <Autocomplete
              multiple
              id={FIELD_ID.tenantIds}
              options={tenants}
              value={chosenTenants}
              onChange={(_event, next) =>
                change(
                  'tenantIds',
                  next.map((tenant) => tenant.id),
                  'tenantIds',
                )
              }
              getOptionLabel={(tenant) => tenant.name}
              getOptionKey={(tenant) => tenant.id}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('announcements.editor.tenants', 'Tenants')}
                  error={errors.tenantIds !== undefined || tenantsFailed}
                  helperText={
                    errors.tenantIds ??
                    (tenantsFailed
                      ? t(
                          'announcements.editor.tenantsLoadError',
                          "Couldn't load the tenants. Choose All customers, or try again later.",
                        )
                      : t(
                          'announcements.editor.tenantsHint',
                          "Only these tenants' members see it.",
                        ))
                  }
                />
              )}
            />
          ) : null}
          <FormControl>
            <FormLabel id="announcement-when-label">
              {t('announcements.editor.publishWhen', 'When to publish')}
            </FormLabel>
            <RadioGroup
              aria-labelledby="announcement-when-label"
              value={draft.when}
              onChange={(event) =>
                change('when', event.target.value === 'schedule' ? 'schedule' : 'now', 'publishAt')
              }
            >
              <FormControlLabel
                value="now"
                control={<Radio />}
                label={t('datedSettings.form.now', 'Now')}
              />
              <FormControlLabel
                value="schedule"
                control={<Radio />}
                label={t('announcements.editor.schedule', 'Schedule')}
              />
            </RadioGroup>
          </FormControl>
          {scheduled ? (
            <TextField
              id={FIELD_ID.publishAt}
              type="datetime-local"
              label={t('announcements.editor.publishAt', 'Publish at (your time)')}
              value={draft.publishAt}
              onChange={(event) => change('publishAt', event.target.value, 'publishAt')}
              error={errors.publishAt !== undefined}
              helperText={errors.publishAt ?? ' '}
              sx={{ maxWidth: 320 }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          ) : null}
          <TextField
            id={FIELD_ID.expires}
            type="datetime-local"
            label={t('announcements.editor.expires', 'Expires (optional, your time)')}
            value={draft.expires}
            onChange={(event) => change('expires', event.target.value, 'expires')}
            error={errors.expires !== undefined}
            helperText={
              errors.expires ??
              t(
                'announcements.editor.expiresHint',
                'Leave it empty to keep it up until you expire it.',
              )
            }
            sx={{ maxWidth: 320 }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </FormSection>

        <FormSection title={t('announcements.editor.sectionDisplay', 'Display')}>
          <FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={draft.pinned}
                  onChange={(event) => change('pinned', event.target.checked)}
                />
              }
              label={t('announcements.editor.pinned', 'Pin to the top')}
            />
            <FormHelperText sx={{ mt: 0 }}>
              {t('announcements.editor.pinnedHint', 'Pinned announcements are listed first.')}
            </FormHelperText>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.popup}
                onChange={(event) => change('popup', event.target.checked)}
              />
            }
            label={t('announcements.editor.popup', 'Show as a pop-up')}
          />
          {draft.popup ? (
            <Alert severity="warning">
              {t(
                'announcements.editor.popupWarning',
                'Shown once, as a pop-up, to everyone in the audience on their next visit.',
              )}
            </Alert>
          ) : null}
          <Autocomplete
            options={relatedOptions}
            value={draft.related}
            onChange={(_event, next) => change('related', next)}
            onInputChange={(_event, next, reason) => {
              if (reason === 'input') setRelatedInput(next);
              if (reason === 'clear') setRelatedInput('');
            }}
            filterOptions={(all) => all}
            getOptionLabel={(option) =>
              option.title === undefined
                ? ticketRef('FEATURE_REQUEST', option.number)
                : `${ticketRef('FEATURE_REQUEST', option.number)} · ${option.title}`
            }
            getOptionKey={(option) => option.number}
            isOptionEqualToValue={(a, b) => a.number === b.number}
            noOptionsText={t('announcements.editor.noOptions', 'No matching feature requests')}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('announcements.editor.related', 'Related feature request (optional)')}
                helperText={t(
                  'announcements.editor.relatedHint',
                  'Its followers see this announcement. Search by number or title.',
                )}
              />
            )}
          />
        </FormSection>
      </FormPage>

      <ConfirmDialog
        open={confirming}
        title={confirmTitle}
        consequence={
          <PublishConsequence
            draft={draft}
            publishAt={publishAtInstant}
            names={chosenTenants.map((tenant) => tenant.name)}
            locale={locale}
          />
        }
        confirmLabel={
          scheduled
            ? t('announcements.confirm.schedule', 'Schedule')
            : t('announcements.confirm.publish', 'Publish')
        }
        {...(stale ? { cancelLabel: t('common.close', 'Close') } : {})}
        confirmDisabled={stale}
        busy={busy}
        error={confirmError}
        onConfirm={() => void publish()}
        onCancel={() => {
          setConfirming(false);
          if (stale) {
            setStale(false);
            onStale(null);
          }
        }}
      />
      <ConfirmDialog
        open={deleting}
        title={t('announcements.delete.title', 'Delete the draft "{{title}}"?', {
          title: initial?.title ?? draft.title.trim(),
        })}
        consequence={t(
          'announcements.delete.consequence',
          "The draft is deleted. This can't be undone.",
        )}
        confirmLabel={t('announcements.delete.confirm', 'Delete draft')}
        {...(stale ? { cancelLabel: t('common.close', 'Close') } : {})}
        destructive
        confirmDisabled={stale}
        busy={busy}
        error={deleteError}
        onConfirm={() => void remove()}
        onCancel={() => {
          setDeleting(false);
          setDeleteError(null);
          if (stale) {
            setStale(false);
            onStale(null);
          }
        }}
      />
    </>
  );
}

function fieldLabel(key: FieldKey, t: Translate): string {
  switch (key) {
    case 'title':
      return t('announcements.editor.title', 'Title');
    case 'body':
      return t('announcements.editor.body', 'Body');
    case 'tenantIds':
      return t('announcements.editor.tenants', 'Tenants');
    case 'publishAt':
      return t('announcements.editor.publishAt', 'Publish at (your time)');
    case 'expires':
      return t('announcements.editor.expires', 'Expires (optional, your time)');
  }
}

/** The publish confirm restates when, to whom, whether it pops up, and when it expires. */
function PublishConsequence({
  draft,
  publishAt,
  names,
  locale,
}: {
  draft: Draft;
  publishAt: string | null;
  names: string[];
  locale: string;
}) {
  const { t } = useCopy();
  const expiresAt = draft.expires === '' ? null : localInputToInstant(draft.expires);
  return (
    <Stack spacing={1}>
      <span>
        {publishAt === null
          ? t('announcements.confirm.whenNow', 'It goes live now.')
          : t('announcements.confirm.whenAt', 'It goes live on {{when}}.', {
              when: formatInstant(publishAt, locale),
            })}
      </span>
      <span>
        {draft.audience === 'ALL'
          ? t('announcements.confirm.toAll', 'Audience: all customers.')
          : t(
              'announcements.confirm.toTenants',
              'Audience: {{count, number}} tenants ({{names}}).',
              {
                count: names.length,
                names: names.join(', '),
              },
            )}
      </span>
      <span>
        {draft.popup
          ? t(
              'announcements.confirm.popupYes',
              'It pops up once for everyone in the audience on their next visit.',
            )
          : t(
              'announcements.confirm.popupNo',
              "It doesn't pop up: members find it in their Support & features panel.",
            )}
      </span>
      {expiresAt !== null ? (
        <span>
          {t('announcements.confirm.expiresAt', 'It expires on {{when}}.', {
            when: formatInstant(expiresAt, locale),
          })}
        </span>
      ) : null}
    </Stack>
  );
}

/** Expire's refusal names the state the server says it has now (`ANNOUNCEMENT_NOT_PUBLISHED {state}`). */
const NOW_STATE: Record<AnnouncementState, string> = {
  DRAFT: 'This one is a draft.',
  SCHEDULED: 'This one is scheduled.',
  PUBLISHED: 'This one is published.',
  EXPIRED: 'This one has already expired.',
};

/** A scheduled, published or expired announcement: shown as sent, with its actions. */
function AnnouncementReadOnly({
  crumbs,
  item,
  notice,
  onChanged,
}: {
  crumbs: Crumbs;
  item: AnnouncementView;
  /** Why it was re-read (a refused save), shown above it. */
  notice: string | null;
  onChanged: () => void;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const notify = useNotify();
  const [action, setAction] = useState<'unpublish' | 'expire' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Expire was refused because it isn't published: its confirm is off; closing re-reads.
  const [stale, setStale] = useState(false);

  const run = async () => {
    if (action === null) return;
    setBusy(true);
    setError(null);
    try {
      if (action === 'unpublish') await adminApi.unpublishAnnouncement(item.id);
      else await adminApi.expireAnnouncement(item.id);
      notify.success(
        action === 'unpublish'
          ? t('announcements.unpublish.done', 'Announcement unpublished.')
          : t('announcements.expire.done', 'Announcement expired.'),
      );
      setAction(null);
      onChanged();
    } catch (failure: unknown) {
      const state = announcementNotPublishedState(failure);
      if (state !== null) setStale(true);
      setError(
        state !== null && Object.hasOwn(NOW_STATE, state)
          ? t(
              'announcements.expire.notPublished',
              'Only a published announcement can be expired. {{stateWords}}',
              {
                stateWords: t(
                  `announcements.expire.nowState.${state}`,
                  NOW_STATE[state as AnnouncementState],
                ),
              },
            )
          : errorCopy(
              failure,
              t,
              action === 'unpublish'
                ? t('announcements.unpublish.error', "Couldn't unpublish it.")
                : t('announcements.expire.error', "Couldn't expire it."),
            ),
      );
    } finally {
      setBusy(false);
    }
  };

  // The actions follow the state the server sent.
  const actions: HeaderAction[] = [
    ...(item.state === 'SCHEDULED' || item.state === 'PUBLISHED'
      ? [
          {
            label: t('announcements.unpublish.action', 'Unpublish…'),
            onClick: () => setAction('unpublish'),
          },
        ]
      : []),
    ...(item.state === 'PUBLISHED'
      ? [
          {
            label: t('announcements.expire.action', 'Expire now…'),
            onClick: () => setAction('expire'),
          },
        ]
      : []),
  ];
  const related =
    item.relatedTicket === undefined ? null : ticketRef('FEATURE_REQUEST', item.relatedTicket);
  const rows: Array<[string, string]> = [
    [t('announcements.col.kind', 'Kind'), kindLabel(item.kind, t)],
    [t('announcements.col.audience', 'Audience'), audienceLabel(item, t)],
    [
      t('announcements.col.publishAt', 'Publishes'),
      item.publishAt === undefined
        ? t('announcements.editor.notScheduled', 'Not scheduled')
        : formatInstant(item.publishAt, locale),
    ],
    [
      t('announcements.col.expiresAt', 'Expires'),
      item.expiresAt === undefined
        ? t('announcements.editor.noExpiry', 'No expiry')
        : formatInstant(item.expiresAt, locale),
    ],
    [
      t('announcements.col.shownAs', 'Shown as'),
      [
        ...(item.pinned ? [t('announcements.pinned', 'Pinned')] : []),
        ...(item.popup ? [t('announcements.popup', 'Pop-up')] : []),
      ].join(' · ') || '—',
    ],
    [t('announcements.col.related', 'Related ticket'), related ?? '—'],
    [t('announcements.col.reads', 'Reads'), formatNumber(item.readCount, locale)],
    // Absent when the staff display name isn't known; never the email (TMMPDM-0111).
    [
      t('announcements.editor.author', 'Written by'),
      item.authorName ?? t('tickets.author.STAFF', 'Staff'),
    ],
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={[...crumbs, { label: item.title }]}
        title={item.title}
        {...(actions.length > 0 ? { secondaryActions: actions } : {})}
      />
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <StatusChip status={item.state} label={stateLabel(item.state, t)} />
          <StatusChip status={item.kind} label={kindLabel(item.kind, t)} />
        </Stack>
        {notice !== null ? <Alert severity="warning">{notice}</Alert> : null}
        <Alert severity="info">
          {item.state === 'EXPIRED'
            ? t('announcements.editor.expiredReadOnly', "An expired announcement can't be changed.")
            : t(
                'announcements.editor.readOnly',
                'Only a draft can be edited. Unpublish it first to change it.',
              )}
        </Alert>
        <SectionCard title={t('announcements.editor.details', 'Details')}>
          <Box
            component="dl"
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'max-content 1fr' },
              columnGap: 3,
              rowGap: 1,
              m: 0,
            }}
          >
            {rows.map(([label, value]) => (
              <Box key={label} sx={{ display: 'contents' }}>
                <Typography component="dt" variant="body2" color="text.secondary">
                  {label}
                </Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        </SectionCard>
        <SectionCard title={t('announcements.editor.body', 'Body')}>
          <MarkdownPreview source={item.body} />
        </SectionCard>
      </Stack>
      <ConfirmDialog
        open={action !== null}
        title={
          action === 'expire'
            ? t('announcements.expire.title', 'Expire "{{title}}" now?', { title: item.title })
            : t('announcements.unpublish.title', 'Unpublish "{{title}}"?', { title: item.title })
        }
        consequence={
          action === 'expire'
            ? t(
                'announcements.expire.consequence',
                'Members stop seeing it. It stays on this list as expired.',
              )
            : t(
                'announcements.unpublish.consequence',
                'Members stop seeing it, and it goes back to a draft you can edit.',
              )
        }
        confirmLabel={
          action === 'expire'
            ? t('announcements.expire.confirm', 'Expire now')
            : t('announcements.unpublish.confirm', 'Unpublish')
        }
        {...(stale ? { cancelLabel: t('common.close', 'Close') } : {})}
        confirmDisabled={stale}
        busy={busy}
        error={error}
        onConfirm={() => void run()}
        onCancel={() => {
          setAction(null);
          setError(null);
          if (stale) {
            setStale(false);
            onChanged();
          }
        }}
      />
    </Box>
  );
}
